import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Profile, Dispo } from '../types';
import { TimeSlotIcon } from '../components/TimeSlotIcons';

interface AvailabilityPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const AvailabilityPage: React.FC<AvailabilityPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDays, setSelectedDays] = useState<Record<string, string[]>>({});
  const [currentDispos, setCurrentDispos] = useState<Dispo[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars

  const jours = [
    { key: 'lundi', label: 'Lundi', icon: '📅' },
    { key: 'mardi', label: 'Mardi', icon: '📅' },
    { key: 'mercredi', label: 'Mercredi', icon: '📅' },
    { key: 'jeudi', label: 'Jeudi', icon: '📅' },
    { key: 'vendredi', label: 'Vendredi', icon: '📅' },
    { key: 'samedi', label: 'Samedi', icon: '🎯' },
    { key: 'dimanche', label: 'Dimanche', icon: '🎯' }
  ];

  const creneaux = [
    { key: 'matin', label: 'Matin', time: '9h-12h' },
    { key: 'apm', label: 'Après-midi', time: '12h-17h' },
    { key: 'soir', label: 'Soir', time: '17h-21h' }
  ];

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data: profileData, error: profileError } = await api.profiles.getByUserId(user!.id);
      
      if (profileError) {
        showToast('Erreur lors du chargement du profil', 'error');
        navigate('/profile');
        return;
      }

      if (profileData) {
        setProfile(profileData);
        
        // Charger les disponibilités depuis la table dispos séparée
        const { data: disposData, error: disposError } = await api.dispos.getByTuteurId(profileData.id);
        
        if (disposError) {
          showToast('Erreur lors du chargement des disponibilités', 'error');
        } else if (disposData && disposData.length > 0) {
          setCurrentDispos(disposData);
          
          // Convertir les dispos en format selectedDays pour l'interface
          const disposByDay: Record<string, string[]> = {};
          disposData.forEach((dispo: Dispo) => {
            if (!disposByDay[dispo.jour]) {
              disposByDay[dispo.jour] = [];
            }
            disposByDay[dispo.jour].push(dispo.creneau);
          });
          setSelectedDays(disposByDay);
        }
      }
    } catch (error) {
      showToast('Une erreur inattendue s\'est produite', 'error');
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user, loadProfile]);

  // Vérifier que l'utilisateur a le droit d'accéder à cette page
  useEffect(() => {
    if (profile && profile.role === 'etudiant') {
      showToast('Cette page est réservée aux tuteur-trices', 'error');
      navigate('/profile');
    }
  }, [profile, navigate, showToast]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleDisponibiliteChange = (jour: string, creneau: string, checked: boolean) => {
    setSelectedDays(prev => {
      const newDays = { ...prev };
      
      if (!newDays[jour]) {
        newDays[jour] = [];
      }
      
      if (checked) {
        if (!newDays[jour].includes(creneau)) {
          newDays[jour] = [...newDays[jour], creneau];
        }
      } else {
        newDays[jour] = newDays[jour].filter(c => c !== creneau);
        if (newDays[jour].length === 0) {
          delete newDays[jour];
        }
      }
      
      return newDays;
    });
  };

  const selectAllDay = (jour: string) => {
    setSelectedDays(prev => ({
      ...prev,
      [jour]: [...creneaux.map(c => c.key)]
    }));
  };

  const clearDay = (jour: string) => {
    setSelectedDays(prev => {
      const newDays = { ...prev };
      delete newDays[jour];
      return newDays;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hp = (document.getElementById('hp_field') as HTMLInputElement | null)?.value;
    if (hp) {
      return;
    }
    
    if (!profile) {
      return;
    }

    // Permettre la sauvegarde même si aucune disponibilité n'est sélectionnée
    // (cela permet de supprimer toutes les disponibilités)
    if (Object.keys(selectedDays).length === 0) {
    }

    setSaving(true);

    try {
      // Convertir selectedDays en array de disponibilités pour la table dispos
      const disponibilites: Array<{jour: string, creneau: string}> = [];
      
      
      Object.entries(selectedDays).forEach(([jour, creneaux]) => {
        
        if (Array.isArray(creneaux)) {
          creneaux.forEach(creneau => {
            if (typeof jour === 'string' && typeof creneau === 'string') {
              disponibilites.push({ jour: jour.trim(), creneau: creneau.trim() });
            } else {
            }
          });
        } else {
        }
      });
      

      const { error } = await api.dispos.updateForTuteur(profile.id, disponibilites);

      if (error) {
        
        // Gestion d'erreurs plus spécifique
        let errorMessage = 'Erreur lors de la mise à jour des disponibilités';
        
        if (error.message) {
          errorMessage = error.message;
        } else if (error.code === '23505') {
          errorMessage = 'Conflit de données - veuillez réessayer';
        } else if (error.code === '42501') {
          errorMessage = 'Permissions insuffisantes';
        } else if (error.hint) {
          errorMessage = `Erreur de base de données: ${error.hint}`;
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      showToast(`Disponibilités mises à jour avec succès ! (${disponibilites.length} créneaux)`, 'success');
      
      // Scroll automatiquement en haut de la page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Rester dans l'onglet disponibilités au lieu de rediriger vers /profile
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  };

  const totalCreneaux = Object.values(selectedDays).reduce((total, creneaux) => total + creneaux.length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-calendar text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mes disponibilités
          </h1>
          <p className="text-gray-600">
            Définissez vos créneaux de disponibilité pour les cours
          </p>
        </div>

        {/* Statistiques */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{Object.keys(selectedDays).length}</div>
              <div className="text-sm text-gray-600">Jours disponibles</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#002c46]">{totalCreneaux}</div>
              <div className="text-sm text-gray-600">Créneaux sélectionnés</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{totalCreneaux * 4}</div>
              <div className="text-sm text-gray-600">Heures/semaine max</div>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Champ honeypot anti-spam (caché) */}
            <input id="hp_field" name="website" type="text" className="hidden" autoComplete="off" tabIndex={-1} aria-hidden="true" />
            <div className="grid gap-6">
              {jours.map(jour => (
                <div key={jour.key} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">{jour.icon}</span>
                      {jour.label}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => selectAllDay(jour.key)}
                        className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                      >
                        <i className="fas fa-check mr-1"></i>
                        Tout sélectionner
                      </button>
                      <button
                        type="button"
                        onClick={() => clearDay(jour.key)}
                        className="text-sm px-3 py-1 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                      >
                        <i className="fas fa-times mr-1"></i>
                        Effacer
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {creneaux.map(creneau => (
                      <label
                        key={creneau.key}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedDays[jour.key]?.includes(creneau.key)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-25'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDays[jour.key]?.includes(creneau.key) || false}
                          onChange={(e) => handleDisponibiliteChange(jour.key, creneau.key, e.target.checked)}
                          className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-3"
                        />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <div className="mr-3">
                              <TimeSlotIcon 
                                timeSlot={creneau.key as 'matin' | 'apm' | 'soir'} 
                                size={20} 
                              />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{creneau.label}</div>
                              <div className="text-sm text-gray-600">{creneau.time}</div>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Conseils */}
            <div className="bg-[#002c46]/5 border border-[#003d5c]/20 rounded-lg p-4">
              <div className="flex items-start">
                <i className="fas fa-info-circle text-[#002c46] mr-3 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-[#001a2a] mb-2">Conseils pour vos disponibilités</h3>
                  <ul className="text-[#001a2a] text-sm space-y-1">
                    <li>• Plus vous avez de créneaux disponibles, plus vous aurez de demandes</li>
                    <li>• Les créneaux de soirée et week-end sont très demandés</li>
                    <li>• Vous pouvez modifier vos disponibilités à tout moment</li>
                    <li>• Pensez à laisser du temps entre vos cours pour vous reposer</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Résumé */}
            {Object.keys(selectedDays).length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-3">
                  <i className="fas fa-check-circle mr-2"></i>
                  Résumé de vos disponibilités
                </h3>
                <div className="space-y-2">
                  {Object.entries(selectedDays).map(([jour, creneauxSelectionnes]) => (
                    <div key={jour} className="flex items-center text-sm">
                      <span className="font-medium capitalize text-green-800 w-20">{jour}:</span>
                      <div className="flex flex-wrap gap-2">
                        {creneauxSelectionnes.map(creneau => {
                          const creneauInfo = creneaux.find(c => c.key === creneau);
                          return (
                            <span key={creneau} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center gap-1">
                              <TimeSlotIcon 
                                timeSlot={creneau as 'matin' | 'apm' | 'soir'} 
                                size={12} 
                              />
                              {creneauInfo?.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Boutons */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving || Object.keys(selectedDays).length === 0}
                className="flex-1 py-3 px-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Sauvegarder ({totalCreneaux} créneaux)
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 